import { type ReactNode, useRef } from 'react';

import { useSlidingPill } from '@apps/web/hooks/use_sliding_pill';
import { cn } from '@apps/web/lib/utils';

type SegmentedControlLabel =
    | ReactNode
    | ((state: { readonly selected: boolean }) => ReactNode);

export type SegmentedControlOption<T extends string> = {
    readonly value: T;
    // Visible content. Pass a function to style by selection (e.g. a count
    // badge that recolors on the active segment).
    readonly label: SegmentedControlLabel;
    // Accessible name for the segment when `label` is not a plain string.
    readonly ariaLabel?: string;
};

type SegmentedControlProps<T extends string> = {
    readonly options: ReadonlyArray<SegmentedControlOption<T>>;
    readonly value: T;
    readonly onValueChange: (value: T) => void;
    readonly disabled?: boolean;
    // 'fill' = equal-width segments in a single row (default, form control).
    // 'auto' = content-width segments that wrap (tab strips).
    readonly layout?: 'fill' | 'auto';
    // 'xs' = dense inline toggle. 'sm' = compact (inline / admin).
    // 'md' = form control (default).
    readonly size?: 'xs' | 'sm' | 'md';
    readonly className?: string;
    readonly 'aria-label'?: string;
};

const SIZE_STYLES: Record<
    'xs' | 'sm' | 'md',
    { readonly track: string; readonly button: string }
> = {
    xs: { track: 'gap-0.5 p-0.5', button: 'h-6 px-2' },
    sm: { track: 'gap-1 p-1', button: 'h-7 px-3' },
    md: { track: 'gap-1 p-1', button: 'h-10 px-3' },
};

/**
 * The "pills inside a muted track" control. A small set of mutually-exclusive
 * options where the active one reads as a raised pill that slides between
 * segments. Use `layout="fill"` for balanced form toggles and `layout="auto"`
 * for content-width tab strips.
 */
export function SegmentedControl<T extends string>({
    options,
    value,
    onValueChange,
    disabled = false,
    layout = 'fill',
    size = 'md',
    className,
    'aria-label': ariaLabel,
}: SegmentedControlProps<T>) {
    const trackRef = useRef<HTMLDivElement>(null);
    const pill = useSlidingPill(trackRef, '[aria-pressed="true"]', value);

    return (
        <div
            ref={trackRef}
            role="group"
            aria-label={ariaLabel}
            className={cn(
                // `isolate` scopes the pill's -z-10 so it paints behind the
                // segment labels but not the page.
                'border-border bg-muted relative isolate rounded-md border',
                SIZE_STYLES[size].track,
                layout === 'fill' ? 'grid' : 'inline-flex max-w-full flex-wrap',
                className,
            )}
            style={
                layout === 'fill'
                    ? {
                          gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`,
                      }
                    : undefined
            }
        >
            {pill !== null && (
                <span
                    aria-hidden="true"
                    className="bg-background absolute -z-10 rounded-sm shadow-sm transition-[left,top,width,height] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
                    style={pill}
                />
            )}
            {options.map((option) => {
                const selected = option.value === value;
                return (
                    <button
                        key={option.value}
                        type="button"
                        disabled={disabled}
                        aria-pressed={selected}
                        aria-label={option.ariaLabel}
                        onClick={() => onValueChange(option.value)}
                        className={cn(
                            'flex min-w-0 items-center justify-center gap-1.5 rounded-sm text-xs font-medium whitespace-nowrap transition-colors',
                            SIZE_STYLES[size].button,
                            selected
                                ? 'text-foreground'
                                : 'text-muted-foreground hover:bg-background/60 hover:text-foreground',
                            disabled &&
                                'hover:text-muted-foreground cursor-not-allowed opacity-70 hover:bg-transparent',
                        )}
                    >
                        {typeof option.label === 'function'
                            ? option.label({ selected })
                            : option.label}
                    </button>
                );
            })}
        </div>
    );
}
